// ─── Puente web → app nativa ────────────────────────────────────────────────
//
// REGLA MÁXIMA: la web NUNCA decide cómo se ve la app (menú de abajo, franja de
// la hora, qué tab se resalta). Solo hace dos cosas:
//   1. Reportar HECHOS de lo que está mostrando (`WEB_STATE`: vista a pantalla
//      completa, modal abierto). La app decide qué hacer con ellos según su
//      configuración (user-management-engine `/app-config`).
//   2. Pedir ACCIONES que la web no puede hacer sola (compartir, calificar,
//      háptica, cerrar la sesión nativa…).
// Nada de "oculta el menú" o "pinta la franja de X color" desde aquí: esa
// lógica vive una sola vez, en la app. Contrato completo:
// heru-mobile-app/docs/app-config.md → "Puente web → app".

import { getStorage } from './general.util';
import STORAGE_KEYS from '../constants/storage';
import { EVENT_TYPES } from '../constants/events';
import { sendEventService } from '../services/events.service';

declare global {
	interface Window {
		ReactNativeWebView?: { postMessage: (message: string) => void };
		/**
		 * Lo inyecta la app antes de cargar la web (binarios ≥ 1.5.0): qué app
		 * es y qué mensajes del puente entiende. Los binarios anteriores no lo
		 * traen — por eso los mensajes nuevos se mandan solo si `bridge` los
		 * lista (`nativeSupports`).
		 */
		HeruApp?: {
			platform: 'ios' | 'android';
			version: string;
			build: string;
			runtimeVersion: string;
			updateId: string;
			bridge: string[];
		};
	}
}

export type HapticStyle =
	| 'light'
	| 'medium'
	| 'heavy'
	| 'soft'
	| 'rigid'
	| 'success'
	| 'warning'
	| 'error'
	| 'selection';

type ModalKind = 'fullscreen' | 'sheet';

type NativeMessage =
	| { type: 'REQUEST_REVIEW' }
	| { type: 'OPEN_STORE_REVIEW' }
	| { type: 'HAPTIC'; style: HapticStyle }
	// Desde aquí, solo binarios que los anuncian en `window.HeruApp.bridge`.
	| { type: 'WEB_STATE'; fullscreen: boolean; modal: ModalKind | null }
	| { type: 'SHARE'; message?: string; url?: string; title?: string }
	| { type: 'OPEN_BROWSER'; url: string }
	| { type: 'OPEN_EXTERNAL'; url: string }
	| { type: 'LOGOUT' }
	| { type: 'SESSION_EXPIRED' }
	| { type: 'REQUEST_PUSH_PERMISSION' }
	| { type: 'OPEN_SETTINGS' };

/**
 * Días mínimos entre SOLICITUDES de review nativo. Es un throttle local
 * deliberadamente laxo (decisión de producto, 2026-06): el racionamiento real
 * lo imponen los sistemas operativos — iOS muestra SKStoreReviewController
 * máximo ~3 veces/año y Play tiene cuotas equivalentes — así que pedirlo a
 * diario no implica mostrarlo a diario.
 */
const REVIEW_COOLDOWN_DAYS = 1;
const DAY_MS = 1000 * 60 * 60 * 24;

/** True si corremos dentro del shell nativo (la app inyecta este objeto). */
export const isNativeApp = (): boolean =>
	typeof window !== 'undefined' && !!window.ReactNativeWebView;

/**
 * True si el binario que nos corre entiende ese mensaje. Los mensajes nuevos del
 * puente se mandan solo con esto en true: un binario viejo los ignora en
 * silencio y la web se quedaría esperando algo que nunca pasa.
 */
export const nativeSupports = (type: NativeMessage['type']): boolean =>
	isNativeApp() && !!window.HeruApp?.bridge?.includes(type);

/**
 * Envía un mensaje al shell nativo (app). No-op en navegador/PWA.
 * Devuelve true si el mensaje se envió.
 *
 * Blindado: el envío NUNCA puede lanzar. Si `postMessage` no existe, el webview
 * lo bloquea (SecurityError) o el payload no es serializable, devolvemos false
 * en silencio — un háptico fallido jamás debe romper el flujo que lo dispara.
 */
export const postToNative = (message: NativeMessage): boolean => {
	if (!isNativeApp()) return false;
	try {
		window.ReactNativeWebView!.postMessage(JSON.stringify(message));
		return true;
	} catch {
		return false;
	}
};

/**
 * Dispara feedback háptico nativo (Taptic Engine en iOS, Vibrator en Android) a
 * través del shell de la app. No-op en navegador/PWA — como el resto del bridge,
 * solo hace algo dentro de la app. Devuelve true si se envió el mensaje.
 *
 * Vocabulario (la app lo mapea a expo-haptics):
 *  - impacto físico:      'light' | 'medium' | 'heavy' | 'soft' | 'rigid'
 *  - resultado:           'success' | 'warning' | 'error'
 *  - cambio de selección: 'selection'
 */
export const triggerHaptic = (style: HapticStyle = 'light'): boolean =>
	postToNative({ type: 'HAPTIC', style });

// ─── Hechos de lo que muestra la web (`WEB_STATE`) ────────────────────────────
//
// Cada vista a pantalla completa y cada modal se registra al montarse y se
// quita al desmontarse; tras cada cambio se manda el estado completo. El primer
// envío (`reportWebReady`, al terminar de cargar el shell) le dice además a la
// app que la web ya decidió qué pantalla mostrar.
//
// Los cambios del mismo commit de React (el cleanup de la vista que sale y el
// effect de la que entra) se juntan en un solo envío, y no se reenvía un estado
// igual al último: la app nunca ve el "sin pantalla completa" intermedio entre
// dos vistas a pantalla completa. El módulo vive lo que vive el documento, igual
// que lo que la app recuerda del último envío (lo olvida al cargar otro).

let fullscreenViews = 0;
let nextModalId = 0;
const openModals = new Map<number, ModalKind>();
let lastSentState: string | null = null;
let flushQueued = false;

const flushWebState = () => {
	flushQueued = false;
	const kinds = [...openModals.values()];
	const state = {
		fullscreen: fullscreenViews > 0,
		modal: kinds.includes('fullscreen') ? 'fullscreen' : kinds.length > 0 ? 'sheet' : null,
	} as const;
	const serialized = JSON.stringify(state);
	if (serialized === lastSentState) return;
	lastSentState = serialized;
	postToNative({ type: 'WEB_STATE', ...state });
};

const reportWebState = () => {
	if (!nativeSupports('WEB_STATE') || flushQueued) return;
	flushQueued = true;
	queueMicrotask(flushWebState);
};

/** El shell autenticado ya resolvió qué pantalla mostrar. */
export const reportWebReady = () => reportWebState();

/** Una vista a pantalla completa está a la vista; devuelve cómo quitarla. */
export const registerFullscreenView = () => {
	fullscreenViews += 1;
	reportWebState();
	return () => {
		fullscreenViews -= 1;
		reportWebState();
	};
};

/** Un modal está a la vista; devuelve cómo quitarlo. */
export const registerModal = (kind: ModalKind) => {
	// Un id por registro: quitar uno nunca puede borrar el de otro modal.
	const id = nextModalId++;
	openModals.set(id, kind);
	reportWebState();
	return () => {
		openModals.delete(id);
		reportWebState();
	};
};

/**
 * Avisa a la app que la sesión web terminó, para que cierre también la suya.
 * Sin esto, la app ve la web caer en /login, recupera la sesión con su refresh
 * token y regresa al usuario adentro: "Cerrar sesión" no cerraba nada.
 *  - 'user': el usuario cerró sesión (o borró su cuenta).
 *  - 'expired': la sesión ya no sirve; la app intenta renovarla antes de sacarlo.
 */
export const notifyNativeLogout = (reason: 'user' | 'expired'): boolean => {
	const type = reason === 'user' ? 'LOGOUT' : 'SESSION_EXPIRED';
	return nativeSupports(type) && postToNative({ type });
};

/**
 * Hoja nativa de compartir. El WebView de Android no tiene `navigator.share`.
 * Devuelve true si la app la va a mostrar; si no, el llamador usa su respaldo.
 */
export const shareNative = (content: { message?: string; url?: string; title?: string }): boolean =>
	nativeSupports('SHARE') && postToNative({ type: 'SHARE', ...content });

/**
 * Opens an https URL outside the WebView: `OPEN_EXTERNAL` hands it to the system
 * browser, `OPEN_BROWSER` to the in-app browser. Returns true if the app will open
 * it; on false (no native app, or a binary without the message) the caller falls
 * back: `openRemoteUrl` navigates the page for the old shell to intercept (inside the
 * app) or opens a tab (in a browser).
 */
export const openOutsideWebViewNative = (
	url: string,
	type: 'OPEN_EXTERNAL' | 'OPEN_BROWSER' = 'OPEN_EXTERNAL',
): boolean => nativeSupports(type) && postToNative({ type, url });

/**
 * Asks for the native push notification permission. If the user has not decided
 * yet, the app shows the system prompt and, if granted, registers the device
 * token; if it was denied, the system does not ask again. Returns true if the app
 * received the request.
 */
export const requestNativePushPermission = (): boolean =>
	nativeSupports('REQUEST_PUSH_PERMISSION') && postToNative({ type: 'REQUEST_PUSH_PERMISSION' });

/**
 * Opens the app's page in the system settings, where the user turns a denied
 * permission back on. Returns true if the app received the request.
 */
export const openNativeAppSettings = (): boolean =>
	nativeSupports('OPEN_SETTINGS') && postToNative({ type: 'OPEN_SETTINGS' });


/**
 * Tracking blindado: cualquier fallo (red, throw, etc.) NUNCA debe romper el
 * flujo de review. Se usa fire-and-forget desde `requestNativeReview`.
 */
const trackSafe = (event: string, eventPayload: Record<string, unknown>) => {
	try {
		sendEventService({
			event,
			source: 'app_native',
			eventPayload,
		}).catch(() => {
			// swallow — el evento es secundario al flujo
		});
	} catch {
		// swallow — el evento es secundario al flujo
	}
};

/**
 * Pide el prompt de calificación nativo (App Store / Play Store) respetando un
 * cooldown local. Solo aplica dentro de la app. Idempotente: una vez disparado,
 * no vuelve a pedir hasta que pasen REVIEW_COOLDOWN_DAYS. Devuelve true si disparó.
 *
 * Tracking (source: "app_native"):
 *  - NATIVE_REVIEW_PROMPT_REQUESTED → cuando se manda el postMessage.
 *  - NATIVE_REVIEW_PROMPT_COOLDOWN_BLOCKED → cuando el cooldown lo bloquea.
 *  Ambos están blindados: cualquier fallo de tracking no afecta el retorno.
 */
export const requestNativeReview = (): boolean => {
	if (!isNativeApp()) return false;

	const storage = getStorage();
	const last = storage.getItem(STORAGE_KEYS.NATIVE_REVIEW_REQUESTED_AT);
	const now = Date.now();

	if (last) {
		const elapsedDays = (now - Number(last)) / DAY_MS;
		if (Number.isFinite(elapsedDays) && elapsedDays < REVIEW_COOLDOWN_DAYS) {
			trackSafe(EVENT_TYPES.NATIVE_REVIEW_PROMPT_COOLDOWN_BLOCKED, {
				last_prompted_at: Number(last),
				days_since_last_prompt: Number(elapsedDays.toFixed(2)),
				cooldown_days: REVIEW_COOLDOWN_DAYS,
				days_remaining: Number((REVIEW_COOLDOWN_DAYS - elapsedDays).toFixed(2)),
			});
			return false;
		}
	}

	const sent = postToNative({ type: 'REQUEST_REVIEW' });
	if (sent) {
		storage.setItem(STORAGE_KEYS.NATIVE_REVIEW_REQUESTED_AT, String(now));
		trackSafe(EVENT_TYPES.NATIVE_REVIEW_PROMPT_REQUESTED, {
			previous_prompted_at: last ? Number(last) : null,
		});
	}
	return sent;
};
